<script setup lang="ts">
import { useDebounceFn, useElementBounding, useVModel, useWindowSize } from "@vueuse/core";
import type { AnimationPlaybackControlsWithThen, Easing, PanInfo } from "motion-v";

import { AnimatePresence, animate, Motion, useMotionValue } from "motion-v";
import type { RendererElement } from "vue";
import { computed, nextTick, onMounted, onUnmounted, ref, toRefs, watch } from "vue";
import { type SheetPresentation, useSheetPresentation } from "./presentation";
import { useSnapPoints } from "./useSnapPoints";
import { clamp, heightPercentToPixels, rubberbandIfOutOfBounds } from "./utils";

interface BottomSheetProps {
	duration?: number;
	snapPoints?: Array<number | `${number}%`>;
	initialSnapPoint?: number;
	blocking?: boolean;
	canSwipeClose?: boolean;
	swipeCloseThreshold?: string | number;
	canBackdropClose?: boolean;
	expandOnContentDrag?: boolean;
	modelValue?: boolean;
	teleportTo?: string | RendererElement;
	teleportDefer?: boolean;
	headerClass?: string;
	contentClass?: string;
	footerClass?: string;
	maxScreenFraction?: number;
	ease?: Easing;
	persistent?: boolean;
	presentation?: SheetPresentation;
}

const props = withDefaults(defineProps<BottomSheetProps>(), {
	blocking: true,
	canSwipeClose: true,
	canBackdropClose: true,
	expandOnContentDrag: true,
	duration: 250,
	teleportTo: "body",
	teleportDefer: false,
	maxScreenFraction: 1,
	ease: "easeInOut",
	persistent: false,
});

const emit = defineEmits<{
	(e: "opened"): void;
	(e: "opening-started"): void;
	(e: "closed"): void;
	(e: "closing-started"): void;
	(e: "ready"): void;
	(e: "dragging-up"): void;
	(e: "dragging-down"): void;
	(e: "snapped", snapPointIndex?: number): void;
	(e: "instinctHeight", instinctHeight: number): void;
	(e: "update:modelValue"): void;
}>();

const sheetPresentation = useSheetPresentation();
const isModal = computed(() => (props.presentation ?? sheetPresentation.value) === "modal");

let closeTimer: ReturnType<typeof setTimeout> | null = null;
const sheetKey = ref(0);
const isDragging = ref(false);
const isVisible = ref(false);

const showSheet = useVModel(props, "modelValue", emit, {
	passive: true,
});

watch(showSheet, (value) => {
	if (value) {
		open();
	} else if (!isClosing) {
		close();
	}
});

onMounted(() => {
	if (showSheet.value) {
		open();
	}
});

const sheet = ref();
const sheetHeader = ref<HTMLElement | null>(null);
const sheetFooter = ref<HTMLElement | null>(null);
const sheetScroll = ref<HTMLElement | null>(null);
const sheetContent = ref<HTMLElement | null>(null);

const preventContentScroll = ref(props.expandOnContentDrag);

const { height: windowHeight } = useWindowSize();
const { height: sheetHeight } = useElementBounding(sheet);
const { height: sheetHeaderHeight } = useElementBounding(sheetHeader);
const { height: sheetContentHeight } = useElementBounding(sheetContent);
const { height: sheetFooterHeight } = useElementBounding(sheetFooter);

const maxScreenHeight = computed(() => windowHeight.value * props.maxScreenFraction);

const instinctHeight = computed({
	get() {
		return clamp(Math.ceil(sheetContentHeight.value + sheetHeaderHeight.value + sheetFooterHeight.value), { max: maxScreenHeight.value });
	},
	set(newValue: number[]) {
		[sheetHeaderHeight.value, sheetContentHeight.value, sheetFooterHeight.value] = newValue;
	},
});

const height = ref(0);
const translateY = ref(0);

const heightValue = useMotionValue(0);
const translateYValue = useMotionValue(0);

const { snapPoints: propSnapPoints } = toRefs(props);
const snapPointsRef = computed(() => propSnapPoints.value ?? [instinctHeight.value]);
const { flattenedSnapPoints, currentSnapPointIndex, closestSnapPointIndex, minSnapPoint, maxSnapPoint } = useSnapPoints(
	snapPointsRef,
	height,
	windowHeight,
	() => props.maxScreenFraction,
);

let controls: AnimationPlaybackControlsWithThen | null;

const preventTouchMove = (event: TouchEvent) => {
	if (event.cancelable) event.preventDefault();
};

const handleTouchMove = (event: TouchEvent) => preventTouchMove(event);

let contentTouchStartX = 0;
let contentTouchStartY = 0;
let contentTouchAxis: "x" | "y" | null = null;

const handleContentTouchStart = (event: TouchEvent) => {
	const touch = event.touches[0];
	if (!touch) return;
	contentTouchStartX = touch.clientX;
	contentTouchStartY = touch.clientY;
	contentTouchAxis = null;
};

const handleSheetScroll = (event: TouchEvent) => {
	if (!preventContentScroll.value) return;

	const touch = event.touches[0];
	if (touch && contentTouchAxis === null) {
		const deltaX = Math.abs(touch.clientX - contentTouchStartX);
		const deltaY = Math.abs(touch.clientY - contentTouchStartY);
		if (deltaX !== 0 || deltaY !== 0) contentTouchAxis = deltaX > deltaY ? "x" : "y";
	}

	if (contentTouchAxis !== "y") return;

	preventTouchMove(event);
};

const handleEscapeKey = (e: KeyboardEvent) => {
	if (e.key === "Escape") close();
};

const backdropClick = () => {
	if (props.canBackdropClose) close();
};

const openModal = async () => {
	if (closeTimer) {
		clearTimeout(closeTimer);
		closeTimer = null;
	}

	isClosing = false;
	showSheet.value = true;
	isVisible.value = true;
	emit("opening-started");
	window.addEventListener("keydown", handleEscapeKey);

	await nextTick();
	emit("opened");
};

const closeModal = () => {
	if (isClosing) return;

	showSheet.value = false;
	isClosing = true;
	emit("closing-started");
	window.removeEventListener("keydown", handleEscapeKey);

	if (closeTimer) clearTimeout(closeTimer);
	closeTimer = setTimeout(() => {
		emit("closed");
		isVisible.value = false;
		isClosing = false;
		closeTimer = null;
	}, props.duration);
};

let isOpening = false;
const open = async () => {
	if (isModal.value) return openModal();
	if (isOpening) return;

	if (isClosing) {
		isClosing = false;
		if (closeTimer) {
			clearTimeout(closeTimer);
			closeTimer = null;
		}
	}

	if (!props.persistent) sheetKey.value++;
	showSheet.value = true;
	isVisible.value = true;
	isOpening = true;
	emit("opening-started");

	try {
		if (props.persistent) {
			if (!sheet.value?.$el) {
				console.warn("[BottomSheet] sheet not ready, aborting");
				showSheet.value = false;
				return;
			}
		} else {
			await nextTick();
			await nextTick();

			const sheetElement = sheet.value?.$el as HTMLElement;
			if (!sheetElement) {
				console.warn("[BottomSheet] sheet not ready, aborting");
				showSheet.value = false;
				return;
			}

			sheetHeight.value = sheetElement.getBoundingClientRect().height;

			const sheetContentElement = sheetElement.querySelector("[data-vsbs-content]") as HTMLElement;
			const sheetHeaderElement = sheetElement.querySelector("[data-vsbs-header]") as HTMLElement;
			const sheetFooterElement = sheetElement.querySelector("[data-vsbs-footer]") as HTMLElement;

			instinctHeight.value = [
				sheetHeaderElement.getBoundingClientRect().height,
				sheetContentElement.getBoundingClientRect().height,
				sheetFooterElement.getBoundingClientRect().height,
			];

			await nextTick();
		}

		currentSnapPointIndex.value = flattenedSnapPoints.value.findIndex((point) => point === minSnapPoint.value);

		if (props.initialSnapPoint) {
			const index = props.initialSnapPoint;
			if (index < 0 || index >= snapPointsRef.value.length) return console.warn("Index out of bounds");
			height.value =
				typeof snapPointsRef.value[index] === "number"
					? clamp(snapPointsRef.value[index] as number, { max: maxScreenHeight.value })
					: heightPercentToPixels(snapPointsRef.value[index] as string, maxScreenHeight.value);
		} else {
			height.value = clamp(minSnapPoint.value, { max: maxScreenHeight.value });
		}

		translateY.value = height.value;
		heightValue.jump(height.value);
		translateYValue.jump(height.value);

		requestAnimationFrame(() => {
			controls = animate(translateYValue, 0, {
				duration: props.duration / 1000,
				ease: props.ease,
				onComplete: () => {
					if (props.blocking) emit("opened");
				},
			});
		});

		window.addEventListener("keydown", handleEscapeKey);
	} finally {
		isOpening = false;
	}
};

let isClosing = false;
const close = () => {
	if (isModal.value) return closeModal();
	if (isClosing) return;

	if (isOpening) isOpening = false;
	if (controls) controls.stop();

	translateYValue.stop();
	heightValue.stop();

	animate(translateYValue, height.value || 500, {
		duration: props.duration / 1000,
		ease: props.ease,
	});

	isDragging.value = false;
	showSheet.value = false;
	isClosing = true;
	emit("closing-started");
	window.removeEventListener("keydown", handleEscapeKey);

	if (closeTimer) clearTimeout(closeTimer);
	closeTimer = setTimeout(() => {
		emit("closed");
		heightValue.set(0);
		isVisible.value = false;
		isClosing = false;
		closeTimer = null;
	}, props.duration);
};

const snapToPoint = (index: number) => {
	if (isModal.value) return;
	if (!snapPointsRef.value) return;

	if (index < 0 || index >= snapPointsRef.value.length) {
		console.warn("Index out of bounds");
		return;
	}

	currentSnapPointIndex.value = index;

	height.value =
		typeof snapPointsRef.value[index] === "number"
			? clamp(snapPointsRef.value[index] as number, { max: maxScreenHeight.value })
			: heightPercentToPixels(snapPointsRef.value[index] as string, maxScreenHeight.value);

	controls = animate(heightValue, height.value, {
		duration: props.duration / 1000,
		ease: props.ease,
		onComplete: () => emit("snapped", snapPointsRef.value.indexOf(snapPointsRef.value[index])),
	});
};

function emitDragDirection(deltaY: number) {
	if (deltaY > 0) {
		emit("dragging-down");
	} else if (deltaY < 0) {
		emit("dragging-up");
	}
}

const handlePanStart = () => {
	isDragging.value = true;
	height.value = sheetHeight.value;
	translateY.value = translateYValue.get();

	heightValue.jump(height.value);
	translateYValue.jump(translateY.value);
};

const handlePan = async (_: PointerEvent, info: PanInfo) => {
	await nextTick();

	if (!sheet.value) return;

	if (translateY.value <= 0) height.value -= info.delta.y;

	if (height.value <= minSnapPoint.value) {
		height.value = minSnapPoint.value;

		translateY.value += info.delta.y;
		isDragging.value = true;

		translateYValue.set(
			props.canSwipeClose ? clamp(translateY.value, { min: 0 }) : clamp(rubberbandIfOutOfBounds(translateY.value, -sheetHeight.value, 0, 0.5), { min: 0 }),
		);
	}

	heightValue.set(clamp(rubberbandIfOutOfBounds(height.value, 0, maxSnapPoint.value, 0.25), { min: 0, max: windowHeight.value }));

	emitDragDirection(info.delta.y);
};

const handlePanEnd = () => {
	if (props.canSwipeClose) {
		let threshold = height.value / 2;

		if (props.swipeCloseThreshold && typeof props.swipeCloseThreshold === "number") threshold = props.swipeCloseThreshold;
		if (props.swipeCloseThreshold && typeof props.swipeCloseThreshold === "string" && props.swipeCloseThreshold.includes("%"))
			threshold = height.value * (Number(props.swipeCloseThreshold.replace("%", "")) / 100);
		if (Math.abs(translateY.value) > threshold) translateY.value = height.value;
	} else {
		translateY.value = 0;
	}

	controls = animate(translateYValue, translateY.value, { duration: props.duration / 1000, ease: props.ease });

	if (translateY.value === height.value) {
		translateY.value = 0;
		close();
		return;
	}

	currentSnapPointIndex.value = closestSnapPointIndex.value;

	height.value =
		typeof snapPointsRef.value[closestSnapPointIndex.value] === "number"
			? clamp(snapPointsRef.value[closestSnapPointIndex.value] as number, { max: maxScreenHeight.value })
			: heightPercentToPixels(snapPointsRef.value[closestSnapPointIndex.value] as string, maxScreenHeight.value);

	controls = animate(heightValue, height.value, {
		duration: props.duration / 1000,
		ease: props.ease,
		onComplete: () => emit("snapped", snapPointsRef.value.indexOf(snapPointsRef.value[closestSnapPointIndex.value])),
	});
	controls = animate(translateYValue, 0, { duration: props.duration / 1000, ease: props.ease });

	isDragging.value = false;
	scrollEnd();
};

const handleContentPanStart = (_: PointerEvent, info: PanInfo) => {
	height.value = sheetHeight.value;
	translateY.value = translateYValue.get();

	if (controls) controls.stop();

	if (!sheetScroll.value) return;

	const isScrollAtTop = sheetScroll.value.scrollTop === 0;
	const isDraggingDown = info.delta.y > 0;
	const hasSingleSnapPoint = flattenedSnapPoints.value.length === 1;
	const isAtTheTop = 0.5 > Math.abs(height.value - maxSnapPoint.value);

	if (hasSingleSnapPoint) {
		if (!props.expandOnContentDrag) return (preventContentScroll.value = false);
		if (translateYValue.get() === 0 && isScrollAtTop && isDraggingDown) preventContentScroll.value = true;
		if (translateYValue.get() === 0 && isScrollAtTop && !isDraggingDown) preventContentScroll.value = false;
	} else {
		if (!props.expandOnContentDrag) return (preventContentScroll.value = false);
		isDragging.value = true;

		preventContentScroll.value = true;
		if (isAtTheTop) {
			if (isDraggingDown && isScrollAtTop) preventContentScroll.value = true;
			if (!isDraggingDown && isScrollAtTop) preventContentScroll.value = false;
			if (!isScrollAtTop) preventContentScroll.value = false;
		}
	}
};

const handleContentPan = async (_: PointerEvent, info: PanInfo) => {
	await nextTick();

	if (!props.expandOnContentDrag) return (preventContentScroll.value = false);
	if (!sheet.value) return;
	if (translateY.value === 0 && preventContentScroll.value && props.expandOnContentDrag) height.value -= info.delta.y;

	if (height.value <= minSnapPoint.value) {
		height.value = minSnapPoint.value;

		if (preventContentScroll.value && props.expandOnContentDrag) translateY.value += info.delta.y;
		translateY.value = clamp(translateY.value, { min: 0, max: minSnapPoint.value });
		translateYValue.set(
			props.canSwipeClose ? clamp(translateY.value, { min: 0 }) : clamp(rubberbandIfOutOfBounds(translateY.value, -sheetHeight.value, 0, 0.5), { min: 0 }),
		);
	}

	if (height.value > maxSnapPoint.value) height.value = maxSnapPoint.value;

	height.value = clamp(height.value, { max: windowHeight.value });

	const hasSingleSnapPoint = flattenedSnapPoints.value.length === 1;
	if (!hasSingleSnapPoint && height.value === maxSnapPoint.value) preventContentScroll.value = false;

	heightValue.set(height.value);

	emitDragDirection(info.delta.y);
};

const scrollEnd = () => {
	if (!sheetScroll.value) return;

	const isScrollAtTop = sheetScroll.value.scrollTop === 0;

	preventContentScroll.value = isScrollAtTop;
};

const debouncedSnapToPoint = useDebounceFn((index) => snapToPoint(index), props.duration);

watch(snapPointsRef, (value, oldValue) => {
	if (showSheet.value === false) return;

	if (!value) return;
	if (!oldValue) return;

	const currentSnapPoint = value[currentSnapPointIndex.value];
	const previousSnapPoint = oldValue[currentSnapPointIndex.value];

	if (typeof currentSnapPoint === "string") return;
	if (typeof previousSnapPoint === "string") return;

	height.value = clamp(currentSnapPoint, { max: maxScreenHeight.value });

	if (currentSnapPoint !== previousSnapPoint) controls = animate(heightValue, height.value, { duration: props.duration / 1000, ease: props.ease });
});

watch(windowHeight, () => !isModal.value && debouncedSnapToPoint(currentSnapPointIndex.value));

defineExpose({ open, close, snapToPoint });

onUnmounted(() => {
	if (closeTimer) clearTimeout(closeTimer);
});
</script>

<template>
	<Teleport :to="teleportTo" :defer="teleportDefer">
		<div v-if="isModal" v-bind="$attrs" data-vsbs-container data-vsbs-modal="true">
			<Transition name="vsbs_modal_backdrop">
				<div v-if="showSheet && blocking" data-vsbs-backdrop @click="backdropClick()" />
			</Transition>

			<Transition name="vsbs_modal">
				<div
					v-if="showSheet || persistent"
					data-vsbs-sheet
					:data-vsbs-sheet-show="showSheet"
					:data-vsbs-hidden="persistent && !showSheet"
					aria-modal="true"
					tabindex="-1"
				>
					<div data-vsbs-header :class="headerClass">
						<slot name="header" />
					</div>
					<div data-vsbs-scroll>
						<div data-vsbs-content-wrapper>
							<div data-vsbs-content :class="contentClass">
								<slot />
							</div>
						</div>
					</div>
					<div data-vsbs-footer :class="footerClass">
						<slot name="footer" />
					</div>
				</div>
			</Transition>
		</div>

		<div v-else v-bind="$attrs" data-vsbs-container>
			<AnimatePresence>
				<Motion
					v-if="showSheet && blocking"
					ref="backdrop"
					:key="'backdrop-' + sheetKey"
					data-vsbs-backdrop
					@click="backdropClick()"
					:transition="{ ease: props.ease, duration: duration / 1000 }"
					:initial="{ opacity: 0 }"
					:animate="{ opacity: 1 }"
					:exit="{ opacity: 0 }"
				/>
			</AnimatePresence>

			<AnimatePresence>
				<Motion
					v-if="showSheet || persistent"
					ref="sheet"
					:exit="{ opacity: 0 }"
					:initial="false"
					:key="sheetKey"
					:style="{ y: translateYValue, z: 1, height: heightValue }"
					:data-vsbs-shadow="!blocking"
					:data-vsbs-hidden="persistent && !isVisible"
					:data-vsbs-sheet-show="showSheet"
					:data-vsbs-dragging="isDragging"
					aria-modal="true"
					data-vsbs-sheet
					tabindex="-1"
				>
					<Motion
						ref="sheetHeader"
						data-vsbs-header
						@pan-start="handlePanStart"
						@pan="handlePan"
						@pan-end="handlePanEnd"
						@touchmove="handleTouchMove"
						:class="headerClass"
					>
						<slot name="header" />
					</Motion>
					<div ref="sheetScroll" data-vsbs-scroll @scrollend="scrollEnd">
						<Motion
							ref="sheetContentWrapper"
							data-vsbs-content-wrapper
							@pan-start="handleContentPanStart"
							@pan="handleContentPan"
							@pan-end="handlePanEnd"
							@touchstart="handleContentTouchStart"
							@touchmove="handleSheetScroll"
						>
							<div ref="sheetContent" data-vsbs-content :class="contentClass">
								<slot />
							</div>
						</Motion>
					</div>
					<Motion
						ref="sheetFooter"
						data-vsbs-footer
						@pan-start="handlePanStart"
						@pan="handlePan"
						@pan-end="handlePanEnd"
						@touchmove="handleTouchMove"
						:class="footerClass"
					>
						<slot name="footer" />
					</Motion>
				</Motion>
			</AnimatePresence>
		</div>
	</Teleport>
</template>

<style scoped lang="scss">
  @use './bottomSheet.scss';
</style>
